import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Tooltip,
  IconButton,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  Avatar,
  Grid,
  Modal,
  Chip,
  Divider,
  Drawer,
  Stack
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { ThemeProvider, createTheme, styled } from '@mui/material/styles';
import {
  Edit,
  Search,
  FilterList,
  AssignmentTurnedIn,
  Pending,
  ErrorOutline,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/userContext.js';
import Layout from '../Layout.js';
import api from '../api.js';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  isSameMonth
} from 'date-fns';
import { toast } from 'react-toastify';
import { localDateKey, parseLocalDate } from '../utils/localDate.js';
import { normalizeTrackerStatusKey, statusIn } from '../utils/trackerNormalization';

/* ===== Minimal, professional theme ===== */
const theme = createTheme({
  palette: {
    primary: { main: '#0F172A' },   // slate-900
    secondary: { main: '#2563EB' }, // blue-600
    success: { main: '#16A34A' },   // green-600
    warning: { main: '#D97706' },   // amber-600
    error: { main: '#DC2626' },     // red-600
    background: { default: '#F6F7FB' }
  },
  typography: {
    fontFamily: `'Inter', system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif`,
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 }
  },
  shape: { borderRadius: 12 }
});

/* ===== Containers / Panels ===== */
const PageWrap = styled('div')(({ theme }) => ({
  background: theme.palette.background.default
}));

const Shell = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(3)
}));

// Minimal header (no white slab / border)
const HeaderBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 0),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'transparent',
  border: 0,
  boxShadow: 'none'
}));

const Panel = styled(Paper)(({ theme }) => ({
  background: '#fff',
  border: '1px solid #E5E7EB',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 6px 20px rgba(15, 23, 42, 0.04)'
}));

/* ===== Metrics ===== */
const MetricCard = styled(Card)(({ theme }) => ({
  background: '#fff',
  border: '1px solid #E5E7EB',
  boxShadow: '0 6px 18px rgba(15,23,42,0.05)',
  cursor: 'pointer'
}));
const MetricIcon = styled(Avatar)(({ color }) => ({
  background: color || '#111827',
  width: 40,
  height: 40
}));

/* ===== Table ===== */
const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: '#F3F4F6',
    color: '#111827',
    fontWeight: 700,
    borderBottom: 0
  }
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: '1px solid #EAEAF2',
  padding: theme.spacing(1.25)
}));
const ZebraRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: '#FAFBFF' },
  transition: 'background-color .15s ease',
  '&:hover': { backgroundColor: '#F4F7FF' }
}));

/* ===== Calendar ===== */
const CalendarCard = styled(Panel)(({ theme }) => ({
  padding: theme.spacing(2)
}));

// Taller & column layout to fit dot row
const DayBox = styled(Box)(({ theme }) => ({
  height: 56,
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #E5E7EB',
  background: '#fff',
  padding: theme.spacing(0.5)
}));

const Dot = styled('span')(({ color }) => ({
  display: 'inline-block',
  width: 7,
  height: 7,
  borderRadius: 999,
  background: color || '#9CA3AF'
}));

const DotRow = styled('div')(() => ({
  display: 'flex',
  gap: 4,
  marginTop: 4,
  minHeight: 8
}));

/* ===== Tracker list ===== */
const TrackerCard = styled(Card)(({ theme, color }) => ({
  border: '1px solid #E5E7EB',
  background: '#fff',
  boxShadow: '0 6px 18px rgba(15,23,42,0.05)',
  borderLeft: `4px solid ${color || '#9CA3AF'}`
}));

/* Status chip colors */
const statusChipSx = (s) => {
  const v = normalizeTrackerStatusKey(s);
  const palette = {
    open: { bg: 'rgba(245, 158, 11, .14)', fg: '#B45309' }, // amber
    'pending-for-review': { bg: 'rgba(59, 130, 246, .14)', fg: '#1D4ED8' }, // blue
    'pending-for-discrepancy': { bg: 'rgba(239, 68, 68, .14)', fg: '#B91C1C' }, // red
    complied: { bg: 'rgba(16, 185, 129, .14)', fg: '#047857' }, // green
    'complied-on-time': { bg: 'rgba(16, 185, 129, .14)', fg: '#047857' },
    delayed: { bg: 'rgba(239, 68, 68, .14)', fg: '#B91C1C' },
    'delayed-compliance': { bg: 'rgba(239, 68, 68, .14)', fg: '#B91C1C' },
    'delayed-complied': { bg: 'rgba(239, 68, 68, .14)', fg: '#B91C1C' },
    'not-applicable': { bg: 'rgba(16, 185, 129, .14)', fg: '#047857' },
    default: { bg: 'rgba(17, 24, 39, .08)', fg: '#111827' }
  };
  return palette[v] || palette.default;
};

const ManagerDashboard = () => {
  const [state] = useAuth();
  const { user } = state;

  // Data & UI states
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchField, setSearchField] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false); // Drawer

  // Filter options & session modal state
  const [companies, setCompanies] = useState([]);
  const [lawAreas, setLawAreas] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);

  const statusOptions = [
    'Open',
    'Pending for Review',
    'Pending for Discrepancy',
    'Completed',
    'Delayed Compliance',
    'Not Applicable',
  ];

  const [filters, setFilters] = useState({
    company: '',
    location: [],
    lawArea: [],
    actOrRule: '',
    status: [],
    startDate: '',
    endDate: '',
  });

  const [stats, setStats] = useState({
    completedTasks: 0,
    reviewerActionable: 0,
    performerActionable: 0,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTrackers, setSelectedTrackers] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [modalPage, setModalPage] = useState(0);
  const [modalRowsPerPage, setModalRowsPerPage] = useState(5);

  // Calendar selected date & its trackers
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDateTrackers, setSelectedDateTrackers] = useState([]);

  const navigate = useNavigate();

  // Helper to truncate text to 50 words
  const truncateText = (text, wordLimit = 50) => {
    if (!text || typeof text !== 'string') return text;
    const words = text.split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  const handleCloseModal = () => setModalOpen(false);

  // Fetch companies & law areas
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const { data } = await api.get('detail/userinfo?view=meta');
        const companyDetails = data.companyDetails || {};
        const companyNames = Array.isArray(companyDetails.company_name)
          ? companyDetails.company_name
          : companyDetails.company_name
            ? [companyDetails.company_name]
            : [];
        setCompanies(companyNames);

        const individualValues = new Set(
          companyDetails.locations?.flatMap?.(obj => Object.values(obj)) || []
        );
        setLocations([...individualValues] || []);

        if (companyNames.length > 0) {
          setFilters((prev) => ({
            ...prev,
            company: companyNames[0],
          }));
        }
      } catch (error) {
        if (error.response && error.response.status === 401) setSessionModalOpen(true);
        console.error('Error fetching companies:', error);
      }
    };

    const fetchLawAreas = async () => {
      try {
        const response = await api.get('tracker/law-area');
        if (response.data?.lawareas) {
          setLawAreas(response.data.lawareas.map((x) => x.law_area));
        }
      } catch (error) {
        if (error.response && error.response.status === 401) setSessionModalOpen(true);
        console.error('Error fetching law areas:', error);
      }
    };

    fetchLawAreas();
    fetchCompanies();
  }, []);

  // Stats
  useEffect(() => {
    const fetchTrackerStats = async () => {
      const { data } = await api.post(
        `reports/get-tracker-details-all-trackers`,
        { user: state?.name }
      );
      setStats({
        completedTasks: data.completedTasks || 0,
        reviewerActionable: data.reviewerActionable || 0,
        performerActionable: data.performerActionable || 0,
      });
    };
    if (user) fetchTrackerStats();
  }, [user, state?.name]);

  // Trackers
  useEffect(() => {
    if (user) fetchTrackers({});
  }, [user]);

  const fetchTrackers = async (filterParams) => {
    try {
      const { data } = await api.post(
        `tracker/get-trackers`,
        {
          company: filters.company,
          user: state?.user,
          ...filterParams,
        }
      );
      setData(data);
      setFilteredData(data);
    } catch (error) {
      toast.error('API request error');
    }
  };

  const trackersByDate = useMemo(() => {
    const mapping = {};
    data.forEach((tracker) => {
      const key = localDateKey(tracker.dueDate);
      (mapping[key] ||= []).push(tracker);
    });
    return mapping;
  }, [data]);
  

  const getTrackersForDate = useCallback(
    (dateObj) => {
      const key = localDateKey(dateObj);
      return trackersByDate[key] || [];
    },
    [trackersByDate]
  );
  const formatDueDate = (date) => format(parseLocalDate(date), 'dd/MM/yyyy');
  const formatCalendarDay = (date) => format(date, 'd');
  const formatSelectedDateHeader = (date) => format(date, 'dd MMM yyyy');
  useEffect(() => {
    setSelectedDateTrackers(getTrackersForDate(selectedDate));
  }, [data, selectedDate, getTrackersForDate]);

  const handleModalChangePage = (_, newPage) => setModalPage(newPage);
  const handleModalChangeRowsPerPage = (e) => { setModalRowsPerPage(parseInt(e.target.value, 10)); setModalPage(0); };

  const handleEdit = (uin) => navigate(`/Edit/${uin}`);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
  const handleSearchFieldChange = (e) => setSearchField(e.target.value);
  const handleSearchValueChange = (e) => setSearchValue(e.target.value);

  // Search
  const handleSearch = () => {
    const trimmed = searchValue.trim();
    if (!trimmed) { setFilteredData(data); return; }
    const searchKey =
      searchField && searchField.trim() !== ''
        ? searchField === 'uin' ? 'uniqueIdentifier' : searchField
        : null;

    if (searchKey) {
      setFilteredData(
        data.filter((row) =>
          (row[searchKey] || '').toString().toLowerCase().includes(trimmed.toLowerCase())
        )
      );
    } else {
      setFilteredData(
        data.filter((row) =>
          Object.keys(row).some((k) => typeof row[k] === 'string' && row[k].toLowerCase().includes(trimmed.toLowerCase()))
        )
      );
    }
  };

  // Filters
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterApply = () => {
    const filterParams = {
      location: filters.location.length ? filters.location : undefined,
      lawArea: filters.lawArea.length ? filters.lawArea : undefined,
      actOrRule: filters.actOrRule || undefined,
      status: filters.status.length ? filters.status : undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    };
    fetchTrackers(filterParams);
    setFilterDialogOpen(false);
  };

  const handleClearFilters = () => {
    setFilters(prev => ({
      company: prev.company, // keep company
      location: [],
      lawArea: [],
      actOrRule: '',
      status: [],
      startDate: '',
      endDate: '',
    }));
  };

  const toggleStatus = (label) => {
    setFilters(prev => {
      const exists = prev.status.includes(label);
      return { ...prev, status: exists ? prev.status.filter(s => s !== label) : [...prev.status, label] };
    });
  };

  const handleCardClick = (type) => {
    let trackers = [];
    if (type === "Completed") {
      trackers = data.filter((t) => statusIn(t.status, [
        "complied", "complied on time", "delayed", "delayed compliance",
        "delayed complied", "not applicable",
      ]));
    } else if (type === "Reviewer Actionable") {
      trackers = data.filter((t) => statusIn(t.status, ["pending for review"]));
    } else if (type === "Performer Actionable") {
      trackers = data.filter((t) => statusIn(t.status, [
        "open", "pending for discrepancy", "in-process", "cannot-comply", "not-complied",
      ]));
    }
    setSelectedTrackers(trackers);
    setModalTitle(type);
    setModalOpen(true);
  };

  // Calendar computations
  const today = new Date();
  const [monthOffset, setMonthOffset] = useState(0);
  const displayedMonth = useMemo(() => addMonths(today, monthOffset), [today, monthOffset]);
  const displayedFirstDayOfMonth = useMemo(() => startOfMonth(displayedMonth), [displayedMonth]);
  const displayedLastDayOfMonth = useMemo(() => endOfMonth(displayedMonth), [displayedMonth]);

  const startOfCalendar = useMemo(() => startOfWeek(displayedFirstDayOfMonth, { weekStartsOn: 0 }), [displayedFirstDayOfMonth]);
  const endOfCalendar = useMemo(() => endOfWeek(displayedLastDayOfMonth, { weekStartsOn: 0 }), [displayedLastDayOfMonth]);

  const calendarDays = useMemo(() => eachDayOfInterval({ start: startOfCalendar, end: endOfCalendar }), [startOfCalendar, endOfCalendar]);

  const weeks = useMemo(() => {
    const w = [];
    for (let i = 0; i < calendarDays.length; i += 7) w.push(calendarDays.slice(i, i + 7));
    return w;
  }, [calendarDays]);

  const handlePrevMonth = () => setMonthOffset((prev) => prev - 1);
  const handleNextMonth = () => setMonthOffset((prev) => prev + 1);

  const finalDisplayData = useMemo(() => {
    const relevantStatuses = ['open', 'pending-for-review', 'pending-for-discrepancy', 'in-process', 'cannot-comply', 'not-complied'];
    return filteredData
      .filter((t) => relevantStatuses.includes(normalizeTrackerStatusKey(t.status)))
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  }, [filteredData]);

  const handleDateClick = (dateObj) => setSelectedDate(dateObj);

  // Colors for tracker list (right panel)
  const statusColor = (s) => {
    const v = normalizeTrackerStatusKey(s);
    if (['open', 'pending-for-discrepancy', 'in-process', 'cannot-comply', 'not-complied'].includes(v)) return theme.palette.warning.main;
    if (v === 'pending-for-review') return theme.palette.secondary.main;
    if (['complied', 'complied-on-time', 'not-applicable'].includes(v)) return theme.palette.success.main;
    if (['delayed', 'delayed-compliance', 'delayed-complied'].includes(v)) return theme.palette.error.main;
    return '#9CA3AF';
  };

  // helpers for calendar dots
  const calendarStatusColor = (s) => {
    const v = normalizeTrackerStatusKey(s);
    if (v === 'open') return theme.palette.warning.main;                  // amber
    if (v === 'pending-for-review') return theme.palette.secondary.main;  // blue
    if (['pending-for-discrepancy', 'cannot-comply', 'not-complied'].includes(v)) return theme.palette.error.main;
    if (['delayed', 'delayed-compliance', 'delayed-complied'].includes(v)) return theme.palette.error.main;
    if (['complied', 'complied-on-time', 'not-applicable'].includes(v))
      return theme.palette.success.main;                                  // green
    return '#9CA3AF';
  };

  if (user?.role === 'admin') {
    return <Navigate to="/dashboard/admin" replace />;
  }

  return (
    <Layout title="Dashboard">
      <ThemeProvider theme={theme}>
        <PageWrap>
          <Shell maxWidth="xl">
            {/* Minimal header (no chunky white bar) */}
            <HeaderBar>
              <Typography variant="h5">My Tasks</Typography>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<FilterList />}
                onClick={() => setFilterDialogOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                Change Filter
              </Button>
            </HeaderBar>

            {/* Metrics (restored) */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <MetricCard onClick={() => handleCardClick('Completed')}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <MetricIcon color={theme.palette.success.main}><AssignmentTurnedIn /></MetricIcon>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Completed Tasks</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>{stats.completedTasks}</Typography>
                    </Box>
                  </CardContent>
                </MetricCard>
              </Grid>
              <Grid item xs={12} sm={4}>
                <MetricCard onClick={() => handleCardClick('Reviewer Actionable')}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <MetricIcon color={theme.palette.warning.main}><Pending /></MetricIcon>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Reviewer Actionable</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>{stats.reviewerActionable}</Typography>
                    </Box>
                  </CardContent>
                </MetricCard>
              </Grid>
              <Grid item xs={12} sm={4}>
                <MetricCard onClick={() => handleCardClick('Performer Actionable')}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <MetricIcon color={theme.palette.error.main}><ErrorOutline /></MetricIcon>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Performer’s Actionable</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>{stats.performerActionable}</Typography>
                    </Box>
                  </CardContent>
                </MetricCard>
              </Grid>
            </Grid>

            {/* Search */}
            <Box
              display="flex"
              alignItems="center"
              mb={2}
              mt={2}
              sx={{ p: 1.25, gap: 1.5, backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: 2 }}
            >
              <FormControl sx={{ minWidth: 160 }} size="small">
                <Select value={searchField} onChange={handleSearchFieldChange} displayEmpty>
                  <MenuItem value="" disabled>Select Field</MenuItem>
                  <MenuItem value="uin">UIN</MenuItem>
                  <MenuItem value="location">Location</MenuItem>
                  <MenuItem value="complianceActivity">Compliance Activity</MenuItem>
                  <MenuItem value="lawArea">Law Area</MenuItem>
                  <MenuItem value="actOrRule">Act or Rule</MenuItem>
                  <MenuItem value="risk">Risk</MenuItem>
                  <MenuItem value="form">Form</MenuItem>
                  <MenuItem value="consequence">Consequence</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Search Value"
                variant="outlined"
                value={searchValue}
                onChange={handleSearchValueChange}
                fullWidth
                size="small"
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                startIcon={<Search />}
                sx={{ px: 2.5, borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
              >
                Search
              </Button>
            </Box>

            {/* Table */}
            <Panel sx={{ mt: 2, overflow: 'hidden' }}>
              <TableContainer>
                <Table stickyHeader size="small">
                  <StyledTableHead>
                    <TableRow>
                      <StyledTableCell>UIN</StyledTableCell>
                      <StyledTableCell>Location</StyledTableCell>
                      <StyledTableCell>Compliance Activity</StyledTableCell>
                      <StyledTableCell>Law Area</StyledTableCell>
                      <StyledTableCell>Act or Rule</StyledTableCell>
                      <StyledTableCell>Due Date</StyledTableCell>
                      <StyledTableCell>Risk</StyledTableCell>
                      <StyledTableCell>Form</StyledTableCell>
                      <StyledTableCell>Consequence</StyledTableCell>
                      <StyledTableCell>Proof</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Details</StyledTableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {finalDisplayData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                      <ZebraRow key={row.uniqueIdentifier}>
                        <StyledTableCell>
                          <Chip
                            onClick={() => {
                              const UIN = JSON.stringify(row.uniqueIdentifier).replace(/"/g, '');
                              handleEdit(UIN);
                            }}
                            clickable
                            label={truncateText(row.uniqueIdentifier)}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#EEF2FF',
                              color: '#4F46E5',
                              '&:hover': { bgcolor: '#E0E7FF' }
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell>{truncateText(row.location)}</StyledTableCell>
                        <StyledTableCell>{truncateText(row.complianceActivity)}</StyledTableCell>
                        <StyledTableCell>{truncateText(row.lawArea)}</StyledTableCell>
                        <StyledTableCell>{truncateText(row.actOrRule)}</StyledTableCell>
                        <StyledTableCell>{formatDueDate(row.dueDate)}</StyledTableCell>
                        <StyledTableCell>{truncateText(row.risk)}</StyledTableCell>
                        <StyledTableCell>{truncateText(row.form)}</StyledTableCell>
                        <StyledTableCell>{truncateText(row.consequence)}</StyledTableCell>
                        <StyledTableCell>{row.proof ? 'Yes' : 'No'}</StyledTableCell>
                        <StyledTableCell>
                          {(() => {
                            const s = statusChipSx(row.status);
                            return (
                              <Chip
                                label={truncateText(row.status)}
                                size="small"
                                sx={{ bgcolor: s.bg, color: s.fg, fontWeight: 600 }}
                              />
                            );
                          })()}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Tooltip title="Edit Detail">
                            <IconButton size="small" color="primary" onClick={() => {
                              const UIN = JSON.stringify(row.uniqueIdentifier).replace(/"/g, '');
                              handleEdit(UIN);
                            }}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </StyledTableCell>
                      </ZebraRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={finalDisplayData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Panel>

            {/* Calendar & Tracker list */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {/* Calendar */}
              <Grid item xs={12} md={4}>
                <CalendarCard>
                  <Typography variant="h6" sx={{ mb: 1 }}>Tracker Calendar</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Button size="small" variant="outlined" onClick={handlePrevMonth} startIcon={<ChevronLeft />} sx={{ textTransform: 'none' }}>
                      Previous
                    </Button>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {format(displayedMonth, 'MMMM yyyy')}
                    </Typography>
                    <Button size="small" variant="outlined" onClick={handleNextMonth} endIcon={<ChevronRight />} sx={{ textTransform: 'none' }}>
                      Next
                    </Button>
                  </Box>
                  <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" sx={{ mb: 1, px: 0.5 }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((w) => (
                      <Typography key={w} variant="caption" align="center" sx={{ fontWeight: 700, color: '#6B7280' }}>
                        {w}
                      </Typography>
                    ))}
                  </Box>
                  {weeks.map((week, weekIndex) => (
                    <Box key={weekIndex} display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.5} sx={{ mb: 0.5 }}>
                      {week.map((dateObj) => {
                        const trackers = getTrackersForDate(dateObj);
                        const inMonth = isSameMonth(dateObj, displayedMonth);

                        const distinctStatuses = Array.from(
                          new Set((trackers || []).map(t => (t?.status ? String(t.status) : '')).map(s => s.toLowerCase().trim()))
                        ).filter(Boolean);

                        const maxDots = 3;
                        const dotsToShow = distinctStatuses.slice(0, maxDots);
                        const overflow = distinctStatuses.length - maxDots;

                        return (
                          <DayBox
                            key={dateObj.toString()}
                            onClick={() => handleDateClick(dateObj)}
                            sx={{ opacity: inMonth ? 1 : 0.45, cursor: 'pointer' }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCalendarDay(dateObj)}
                            </Typography>

                            {distinctStatuses.length > 0 && (
                              <DotRow>
                                {dotsToShow.map((s) => (
                                  <Dot key={s} color={calendarStatusColor(s)} title={s} />
                                ))}
                                {overflow > 0 && (
                                  <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1 }}>
                                    +{overflow}
                                  </Typography>
                                )}
                              </DotRow>
                            )}
                          </DayBox>
                        );
                      })}
                    </Box>
                  ))}
                </CalendarCard>
              </Grid>

              {/* Tracker Window */}
              <Grid item xs={12} md={8}>
                <Panel sx={{ p: 2 }}>
                  <Typography variant="h6">
                    Trackers for {formatSelectedDateHeader(selectedDate)}
                  </Typography>
                  {selectedDateTrackers.length ? (
                    selectedDateTrackers.map((tracker) => {
                      const c = statusColor(tracker.status);
                      return (
                        <TrackerCard key={tracker.uniqueIdentifier} color={c} sx={{ mb: 1.5 }}>
                          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: theme.palette.secondary.main, cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => handleEdit(tracker.uniqueIdentifier)}
                            >
                              {tracker.uniqueIdentifier}
                            </Typography>
                            <Typography variant="body2">
                              {tracker.complianceActivity}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDueDate(tracker.dueDate)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {tracker.status}
                              </Typography>
                            </Box>
                          </CardContent>
                        </TrackerCard>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No trackers for this date.
                    </Typography>
                  )}
                </Panel>
              </Grid>
            </Grid>

            {/* Modal */}
            <Modal
              open={modalOpen}
              onClose={handleCloseModal}
              aria-labelledby="modal-title"
              aria-describedby="modal-description"
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "86%",
                  maxHeight: "80vh",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  borderRadius: 2,
                  p: 2.5,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h6" id="modal-title" gutterBottom sx={{ fontWeight: 700 }}>
                  {modalTitle} Trackers
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ flexGrow: 1, overflow: "auto", mb: 2 }}>
                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>UIN</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Compliance Activity</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedTrackers.length > 0 ? (
                          selectedTrackers
                            .slice(
                              modalPage * modalRowsPerPage,
                              modalPage * modalRowsPerPage + modalRowsPerPage
                            )
                            .map((tracker, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    onClick={() => handleEdit(tracker.uniqueIdentifier)}
                                    sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }}
                                  >
                                    {tracker.uniqueIdentifier}
                                  </Typography>
                                </TableCell>
                                <TableCell>{tracker.complianceActivity}</TableCell>
                                <TableCell>{formatDueDate(tracker.dueDate)}</TableCell>
                                <TableCell>{tracker.status}</TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No Trackers Found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={selectedTrackers.length}
                    rowsPerPage={modalRowsPerPage}
                    page={modalPage}
                    onPageChange={handleModalChangePage}
                    onRowsPerPageChange={handleModalChangeRowsPerPage}
                  />
                  <Button onClick={handleCloseModal} variant="contained">
                    Close
                  </Button>
                </Box>
              </Box>
            </Modal>

            {/* ====== RIGHT-SIDE FILTER DRAWER ====== */}
            <Drawer
              anchor="right"
              open={filterDialogOpen}
              onClose={() => setFilterDialogOpen(false)}
              keepMounted
              PaperProps={{
                sx: {
                  width: { xs: '100%', sm: 420 },
                  p: 2,
                  borderLeft: '1px solid #E5E7EB'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Filters</Typography>
                <Button size="small" color="inherit" onClick={handleClearFilters}>Clear all</Button>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1.25}>
                {/* Company */}
                <FormControl size="small" fullWidth>
                  <InputLabel id="company-select-label">Company</InputLabel>
                  <Select
                    labelId="company-select-label"
                    name="company"
                    label="Company"
                    value={filters.company}
                    onChange={handleFilterChange}
                  >
                    {companies.map((company, idx) => (
                      <MenuItem key={idx} value={company}>{company}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Location (chips) */}
                <Autocomplete
                  multiple
                  size="small"
                  options={locations}
                  value={filters.location}
                  onChange={(_, newValue) => setFilters(prev => ({ ...prev, location: newValue }))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option} size="small" label={option} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label="Location" placeholder="Select locations" />}
                />

                {/* Law Area (chips) */}
                <Autocomplete
                  multiple
                  size="small"
                  options={lawAreas}
                  value={filters.lawArea}
                  onChange={(_, newValue) => setFilters(prev => ({ ...prev, lawArea: newValue }))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option} size="small" label={option} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label="Law Area" placeholder="Select law areas" />}
                />

                {/* Act or Rule */}
                <TextField
                  size="small"
                  label="Act or Rule"
                  name="actOrRule"
                  value={filters.actOrRule}
                  onChange={handleFilterChange}
                />

                {/* Status quick toggles */}
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Status</Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {statusOptions.map((label) => {
                      const active = filters.status.includes(label);
                      const s = statusChipSx(label);
                      return (
                        <Chip
                          key={label}
                          label={label}
                          size="small"
                          onClick={() => toggleStatus(label)}
                          variant={active ? 'filled' : 'outlined'}
                          sx={{
                            bgcolor: active ? s.bg : 'transparent',
                            color: active ? s.fg : 'inherit',
                            borderColor: '#E5E7EB',
                            fontWeight: active ? 700 : 500
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>

                {/* Date Range */}
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Start Date"
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="End Date"
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
              </Stack>

              {/* Sticky footer actions */}
              <Box
                sx={{
                  position: 'sticky',
                  bottom: -2,
                  left: 0,
                  right: 0,
                  mt: 2,
                  pt: 1.5,
                  background: 'linear-gradient(to top, #fff, rgba(255,255,255,0.75))',
                  borderTop: '1px solid #E5E7EB'
                }}
              >
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button onClick={() => setFilterDialogOpen(false)} color="inherit">Cancel</Button>
                  <Button onClick={handleFilterApply} variant="contained" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                    Apply Filters
                  </Button>
                </Stack>
              </Box>
            </Drawer>
          </Shell>
        </PageWrap>
      </ThemeProvider>
    </Layout>
  );
};

export default ManagerDashboard;
